import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { EnforcementMode } from "@/pages/admin/apikeys/hooks/useApiKeys";

interface ApiPolicySwitchProps {
  policy: EnforcementMode;
  loading: boolean;
  onChange: (mode: EnforcementMode) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

// 校验接口鉴权策略开关：在「强制 Key」与「过渡兼容」之间切换，方便平滑迁移旧客户端。
// 为避免误触影响线上客户端，切换前会弹出二次确认，明确展示「当前 → 目标」与风险提示。
export default function ApiPolicySwitch({ policy, loading, onChange, notify }: ApiPolicySwitchProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<EnforcementMode | null>(null);

  const options: { key: EnforcementMode; label: string; icon: string }[] = [
    { key: "required", label: t("apikeys.policyRequired"), icon: "ri-lock-2-line" },
    { key: "optional", label: t("apikeys.policyOptional"), icon: "ri-lock-unlock-line" },
  ];

  const labelOf = (mode: EnforcementMode) =>
    mode === "required" ? t("apikeys.policyRequired") : t("apikeys.policyOptional");

  const confirmSwitch = async () => {
    if (!pending) return;
    const mode = pending;
    setSaving(true);
    try {
      await onChange(mode);
      notify(t("apikeys.policySwitched"), "success");
      setPending(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : t("apikeys.policySwitchFailed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const transitional = policy === "optional";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-md ${
              transitional ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
            }`}
          >
            <i className={transitional ? "ri-lock-unlock-line" : "ri-shield-keyhole-line"} />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apikeys.policyTitle")}</h2>
            <p className="text-[11px] text-foreground-500">{t("apikeys.policySub")}</p>
          </div>
        </div>

        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {options.map((option) => {
            const active = policy === option.key;
            return (
              <button
                key={option.key}
                type="button"
                disabled={loading || saving}
                onClick={() => {
                  if (option.key === policy) return;
                  setPending(option.key);
                }}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  active
                    ? option.key === "required"
                      ? "bg-primary-500 text-background-50"
                      : "bg-accent-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-50"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={option.icon} />
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className={`mt-4 flex items-start gap-2.5 rounded-md border px-3.5 py-3 ${
          transitional ? "border-accent-200 bg-accent-50" : "border-background-200 bg-background-100"
        }`}
      >
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center ${
            transitional ? "text-accent-700" : "text-primary-700"
          }`}
        >
          <i className={transitional ? "ri-alert-line" : "ri-information-line"} />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium text-foreground-900">
            {t("apikeys.policyCurrent")}：
            <span className={transitional ? "text-accent-800" : "text-primary-700"}>{labelOf(policy)}</span>
          </p>
          <p className="mt-1 text-xs leading-relaxed text-foreground-600">
            {transitional ? t("apikeys.policyOptionalDesc") : t("apikeys.policyRequiredDesc")}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-foreground-500">
            {transitional ? t("apikeys.policyOptionalWarn") : t("apikeys.policyRequiredWarn")}
          </p>
        </div>
      </div>

      {pending && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-foreground-950/40 px-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-alert-line text-lg" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">
                {t("apikeys.policyConfirmTitle")}
              </h3>
            </div>

            <div className="mt-4 flex items-center justify-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-4">
              <span className="rounded-full bg-background-200 px-3 py-1 text-xs font-medium text-foreground-700">
                {labelOf(policy)}
              </span>
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-line" />
              </span>
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  pending === "required" ? "bg-primary-500 text-background-50" : "bg-accent-500 text-background-50"
                }`}
              >
                {labelOf(pending)}
              </span>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-foreground-600">
              {pending === "required" ? t("apikeys.policyConfirmToRequired") : t("apikeys.policyConfirmToOptional")}
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setPending(null)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("apikeys.cancel")}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void confirmSwitch()}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={saving ? "ri-loader-4-line animate-spin" : "ri-check-line"} />
                </span>
                {saving ? t("apikeys.saving") : t("apikeys.policyConfirmBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}